// eslint-disable-next-line import/named
import {
  Floor,
  FloorCompressed,
  FloorMarkerResourceDto,
  ResourceFloorDto,
  ResourceHeatmapValueDto,
} from '../model';
import { DateTime } from 'luxon';
import { axiosManager } from '../config';
import { onNextPage } from '../utils';

export interface IGetFloorResourcesParameters {
  name?: string;
  capacity?: number;
  bookable?: boolean;
  visible?: boolean;
  resourceTypeId?: number[];
  ownerId?: number[];
  requestOwnerValidation?: boolean;
  from?: DateTime;
  to?: DateTime;
  labels?: string[];
  synchronizedOnly?: boolean;
  favoritesOnly?: boolean;
}

export interface IGetFloorHeatmapParameters {
  type: 'occupation' | 'booking' | 'occupation-perceived';
  resourceTypeIds?: number[];
}

export interface IGetFloorsParameters {
  floorName?: string;
  locationId?: number;
  page?: string;
  size?: number;
  sort?: GetFloorsSortParamsEnum[];
  desc?: GetFloorsSortParamsEnum[];
}

export enum GetFloorsSortParamsEnum {
  FloorNum = 'floorNum',
}

export const FloorService = {
  path: 'floors',

  getFloor(id: number): Promise<FloorCompressed> {
    const url = `${this.path}/${id}`;

    return axiosManager
      .getInstance()
      .get(url)
      .then((res) => res.data);
  },

  getFloorPlan(id: number): Promise<Floor> {
    const url = `${this.path}/${id}/floor_plan`;

    return axiosManager
      .getInstance()
      .get(url)
      .then((res) => res.data)
      .catch((error) => {
        console.error(error);
        throw error;
      });
  },

  getFloorResourceMarkers(id: number, options?: IGetFloorResourcesParameters): Promise<FloorMarkerResourceDto[]> {
    const params = this.createGetFloorResourcesParams(options);
    // This does not return the status !
    const url = `${this.path}/${id}/floor_plan/markers/resource`;

    return axiosManager
      .getInstance()
      .get(url, { params })
      .then((res) => res.data);
  },

  getFloorResourceTagMarkers(id: number, options?: IGetFloorResourcesParameters): Promise<unknown[]> {
    const params = this.createGetFloorResourcesParams(options);
    // This does not return the status !
    const url = `${this.path}/${id}/floor_plan/markers/resource_tag`;

    return axiosManager
      .getInstance()
      .get(url, { params })
      .then((res) => res.data);
  },

  getFloorPlanResources(id: number, options?: IGetFloorResourcesParameters): Promise<ResourceFloorDto[]> {
    const params = this.createGetFloorResourcesParams(options);
    const url = `${this.path}/${id}/resources`;

    return axiosManager
      .getInstance()
      .get(url, { params })
      .then((res) => res.data);
  },

  getFloorPlanHeatmap(id: number, options: IGetFloorHeatmapParameters): Promise<ResourceHeatmapValueDto[]> {
    const params = this.createGetFloorHeatmapParams(options);

    if (!options.type) {
      throw new Error('Heatmap type is required');
    }

    const appendix = options.type.startsWith('occupation') ? 'occupation' : options.type;
    const url = `${this.path}/${id}/heatmap/${appendix}`;

    return axiosManager
      .getInstance()
      .get(url, { params })
      .then((res) => res.data);
  },

  getFloorsWithPagination(options?: IGetFloorsParameters): Promise<{
    data: FloorCompressed[];
    nextPage: string;
  }> {
    const params = this.createGetFloorsParams(options);
    const page = params?.get('page');

    if (page) {
      return axiosManager.getInstance().get(page).then(onNextPage);
    }

    const url = `${this.path}`;

    return axiosManager.getInstance().get(url, { params }).then(onNextPage);
  },

  createGetFloorsParams(options?: IGetFloorsParameters): URLSearchParams {
    const params = new URLSearchParams();

    if (!options) {
      return params;
    }

    if (options.floorName) {
      params.set('floorName', options.floorName);
    }

    if (options.locationId) {
      params.set('locationId', options.locationId.toString());
    }

    if (options.page) {
      params.set('page', options.page);
    }

    if (options.size) {
      params.set('size', options.size.toString());
    }

    if (options.sort && options.sort.length > 0) {
      params.set('sort', options.sort.join(','));
    }

    if (options.desc && options.desc.length > 0) {
      params.set('desc', options.desc.join(','));
    }

    return params;
  },

  createGetFloorResourcesParams(options?: IGetFloorResourcesParameters): URLSearchParams {
    const params = new URLSearchParams();

    if (!options) {
      return params;
    }

    if (options.name) {
      params.set('name', options.name);
    }

    if (options.capacity) {
      params.set('capacity', options.capacity.toString());
    }

    if (typeof options.bookable === 'boolean') {
      params.set('bookable', options.bookable.toString());
    }

    if (typeof options.visible === 'boolean') {
      params.set('visible', options.visible.toString());
    }

    if (options.resourceTypeId && options.resourceTypeId.length > 0) {
      params.set('resourceTypeId', options.resourceTypeId.join(','));
    }

    if (options.ownerId && options.ownerId.length > 0) {
      params.set('ownerId', options.ownerId.join(','));
    }

    if (typeof options.requestOwnerValidation === 'boolean') {
      params.set('requestOwnerValidation', options.requestOwnerValidation.toString());
    }

    if (options.from) {
      params.set('from', options.from.toUTC().toISO() ?? 'Invalid Date');
    }

    if (options.to) {
      params.set('to', options.to.toUTC().toISO() ?? 'Invalid Date');
    }

    if (options.labels && options.labels.length > 0) {
      params.set('labels', options.labels.join(','));
    }

    if (typeof options.synchronizedOnly === 'boolean') {
      params.set('synchronizedOnly', options.synchronizedOnly.toString());
    }

    if (typeof options.favoritesOnly === 'boolean') {
      params.set('favoritesOnly', options.favoritesOnly.toString());
    }

    return params;
  },

  createGetFloorHeatmapParams(options?: IGetFloorHeatmapParameters): URLSearchParams {
    const params = new URLSearchParams();

    if (options?.type === 'occupation-perceived') {
      params.set('perceived', 'true');
    }

    if (options?.resourceTypeIds && options.resourceTypeIds.length > 0) {
      params.set('resourceTypeId', options.resourceTypeIds.join(','));
    }

    return params;
  },
};
